import { PlayerEvents, HeartbeatEvents, PlaybackMode, playerStateManager } from '@4tn/core-audio-player-shared';
import type Media from '@api/models/Media';
import { INITIAL_CONFIG } from '../consts';
import logger from '@utils/logger';
import ConnectionManager from './connection-manager';
import { TRITON_PLAYER, TRITON_PLAYER_ID } from './consts';
import { type ITritonSDK, type MediaPlayerModule, type TrackingParams, type ITritonPlayer } from './types';
import EventDispatcher from '@utils/event-dispatcher';
import { type IConfig } from '../types';
import { isOfflineOnFirefox } from '../utils/browser';
import executePoll from '@utils/execute-poll';
import { getUriFromMediaObject } from '@players/CoreAudioPlayer/utils';
import { prerollConsentTag } from '@players/VastPlayer/utils';

class TritonPlayer implements ITritonPlayer {
  static instance: TritonPlayer;

  static active: boolean;

  protected readyInternal = false;

  private player: ITritonSDK;

  private config: IConfig = INITIAL_CONFIG;

  private station = '';

  private media: Media;

  /*
  we need previousMedia to keep track of previously played triton stream
  because this.media is used inside stream_stop listener which is triggered asynchronously
  which means it won't have correct value at that time
  */
  private previousMedia: Media;

  private playerLoaded = false;

  private configurationTimer: ReturnType<typeof setTimeout>;

  private readonly uri: string;

  private readonly connectionManager: ConnectionManager;

  private constructor() {
    this.connectionManager = new ConnectionManager(this);
  }

  static getInstance(): ITritonPlayer {
    if (!TritonPlayer.instance) {
      TritonPlayer.instance = new TritonPlayer();
    }
    TritonPlayer.instance.connectionManager.handleConnections();
    return TritonPlayer.instance;
  }

  public handlePlay(uri: string, config: IConfig, media: Media, noPreroll: boolean): Promise<void> {
    // "talpa_consent" is a mandatory parameter that needs to be provided for all Triton streams
    // This should apply to both Juke web and Partner Player apps on our end
    // To ensure compliance, we should include either "talpa_consent:1" or "talpa_consent:0" in the data sent to Triton
    // We need to disable this rule, because TS doesn't recognize template expression as string
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const talpaConsent = `talpa_consent:${prerollConsentTag(config?.gdprConsent)}`;
    const exculdePreroll = 'player:nopreroll';

    // In the case of the Partner Player, where we currently sending the "player:nopreroll" when we want to exclude rule for prerolls
    // In that case we should include both "talpa_consent" and "player:nopreroll" values together by separating them with a comma and also send as part of ttag parameter
    const ttagValue = noPreroll ? `${talpaConsent},${exculdePreroll}` : talpaConsent;
    this.config = { ...config, ttag: ttagValue };
    this.previousMedia = this.media;
    this.media = media;

    if (!this.playerLoaded) {
      return this.loadPlayerAndPlay(uri);
    }
    return this.handleUpdateSrc(uri);
  }

  public async destroy(): Promise<void> {
    clearTimeout(this.configurationTimer);
    if (!this.player) {
      return;
    }
    this.removeEventListeners();
    await executePoll(() => typeof this.player?.destroy === 'function');
    this.player.destroy();
    this.readyInternal = false;
  }

  public onPlayerReady(): void {
    this.readyInternal = true;
    this.addEventListeners();
  }

  public tritonConfigure(uri: string): Promise<void> {
    this.station = uri;

    const urlSearchParams = new URLSearchParams(window.location.search);

    const age = urlSearchParams.get('age') ?? '';
    const dob = urlSearchParams.get('dob') ?? '';
    const yob = urlSearchParams.get('yob') ?? '';
    const gender = urlSearchParams.get('gender') ?? '';
    const ip = urlSearchParams.get('ip') ?? '';

    const idSyncStation = urlSearchParams.get('idSyncStation') ?? this.station;

    // Triton Digital player configuration
    const config = {
      coreModules: [],
      playerReady: () => {},
    };

    // MediaPlayer module
    const mediaPlayerModule: MediaPlayerModule = {
      id: 'MediaPlayer',
      playerId: TRITON_PLAYER_ID,
      techPriority: ['Html5'],
      isDebug: false,
      playerServicesRegion: 'eu',
      geoTargeting: false,
      sbm: {
        active: true,
        aSyncCuePointFallback: true,
      },
      idSync: {
        station: idSyncStation,
        age,
        dob,
        yob,
        gender,
        ip,
      },
    };

    if (this.config.gdpr) {
      mediaPlayerModule.idSync.gdpr = this.config.gdpr;
    }

    if (this.config.gdprConsent) {
      mediaPlayerModule.idSync.gdpr_consent = this.config.gdprConsent;
    }

    if (this.media.audioHls) {
      mediaPlayerModule.hls = true;
    }

    // Add the MediaPlayer module
    config.coreModules.push(mediaPlayerModule as never);

    return new Promise<void>((resolve) => {
      config.playerReady = () => {
        this.onPlayerReady();
        resolve();
      };
      this.player = new window.TDSdk(config);
    });
  }

  public play(): void {
    if (!this.isReady()) {
      return;
    }

    const params: TrackingParams = {};

    params.dist = this.config?.distTag;
    if (this.config?.gdpr) {
      params.gdpr = this.config.gdpr;
    }

    if (this.config?.ttag) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      params.ttag = `${this.config.ttag}`;
    }

    if (this.config?.gdprConsent) {
      params.gdpr_consent = this.config.gdprConsent;
    }

    // Expect to get userToken and hdStream flag for HD streams
    if (this.config?.userToken && this.config?.hdStream) {
      params.tdtok = this.config.userToken;
    }

    const { volumeLevel } = playerStateManager.getState();
    this.player?.setVolume(volumeLevel ?? 1);

    this.player?.play?.({
      mount: this.station,
      trackingParameters: params,
    });
  }

  public async resume(): Promise<void> {
    throw new Error('Method resume not implemented.');
  }

  public async stop(): Promise<void> {
    clearTimeout(this.configurationTimer);
    // every destroy() call will remove Triton's instance which means that all methods from this.player will be temporarily unset until Triton is asynchronously loaded again
    // so we need to wait for Triton's stop method to be set, so we are always sure that the correct stream is stopped
    await executePoll(() => typeof this.player?.stop === 'function');
    const node = this.getPlayerNode();
    node?.pause();
    this.player.stop();
  }

  public pause(): void {
    const playerNode = this.getPlayerNode();
    if (!this.player || !playerNode) {
      return;
    }

    playerNode.pause();
    EventDispatcher.sendEvent(PlayerEvents.STREAM_PAUSE, {
      name: PlayerEvents.STREAM_PAUSE,
      player: TRITON_PLAYER,
      media: this.media,
      time: this.currentTime,
    });
  }

  public mute(): void {
    if (!this.isReady()) {
      return;
    }
    this.player?.mute?.();

    EventDispatcher.sendEvent(PlayerEvents.STREAM_MUTED, {
      name: PlayerEvents.STREAM_MUTED,
      player: TRITON_PLAYER,
    });

    /**
     * If the Triton player is muted, it will generate the stream-stop event,
     * which must be monitored through Tealium. However, since this event is not
     * unique among other system stop events, a custom player event is required to
     * distinguish it. This custom event is not necessary for Heartbeat tracking,
     * as the system stop event sent from the player will suffice for Heartbeat's purposes.
     */
    EventDispatcher.sendEvent(PlayerEvents.STREAM_MUTE_STOP, {
      name: PlayerEvents.STREAM_MUTE_STOP,
      player: TRITON_PLAYER,
    });
  }

  public unmute(): void {
    if (!this.isReady()) {
      return;
    }
    this.player?.unMute?.();

    /**
     * If the Triton player is unmuted, it won't trigger the system start event.
     * So we need to trigger it manually, to be able to track it through Tealium.
     * Custom player event is required, so we can easily distinguish it in the Analytics package.
     * The event should not be relevant for the Heartbeat tracking.
     */
    EventDispatcher.sendEvent(PlayerEvents.STREAM_UNMUTE_START, {
      name: PlayerEvents.STREAM_UNMUTE_START,
      player: TRITON_PLAYER,
      isLiveAudio: true,
      uri: this.uri,
      media: this.media,
    });

    EventDispatcher.sendEvent(PlayerEvents.STREAM_UNMUTED, {
      name: PlayerEvents.STREAM_UNMUTED,
      player: TRITON_PLAYER,
    });
  }

  public setVolume(level: number): void {
    if (!this.isReady()) {
      return;
    }
    this.player?.setVolume?.(level);

    EventDispatcher.sendEvent(PlayerEvents.VOLUME_CHANGED, {
      name: PlayerEvents.VOLUME_CHANGED,
      player: TRITON_PLAYER,
      volume: level,
    });
  }

  public seek(intent: number): void {
    if (!this.isReady()) {
      return;
    }
    this.player?.seek?.(intent);
  }

  public isReady(): this is { readyInternal: boolean } {
    return this.readyInternal;
  }

  private loadPlayerAndPlay(uri: string): Promise<void> {
    return this.tritonConfigure(uri).then(() => {
      this.playerLoaded = true;

      if (TritonPlayer.active) {
        this.play();
      }
    });
  }

  private handleUpdateSrc(uri: string): Promise<void> {
    clearTimeout(this.configurationTimer);
    return new Promise((resolve) => {
      // this timeout is needed because Triton SDK's destroy() method uses timeout for 500ms internally
      this.configurationTimer = setTimeout(() => {
        void this.tritonConfigure(uri).then(resolve);
      }, 500);
    }).then(() => {
      if (TritonPlayer.active) {
        this.play();
      }
    });
  }

  private isTritonUnmounted(): boolean {
    const uri = getUriFromMediaObject(this.media, this.config);
    return !this.isReady() || !TritonPlayer.active || uri !== this.station;
  }

  private addEventListeners(): void {
    if (!this.player) {
      return;
    }
    this.player.addEventListener?.(PlayerEvents.STREAM_START, this.onStreamStart.bind(this));
    this.player.addEventListener?.(PlayerEvents.STREAM_STOP, this.onStreamStop.bind(this));
    this.player.addEventListener?.(PlayerEvents.STREAM_STATUS, this.onStreamStatusChange.bind(this));
    this.player.addEventListener?.(PlayerEvents.STREAM_CONFIG_ERROR, this.onStreamConfigError.bind(this));
    this.player.addEventListener?.(PlayerEvents.STREAM_FAIL, this.onStreamFail.bind(this));
    this.player.addEventListener?.(PlayerEvents.STREAM_ERROR, this.onStreamError.bind(this));
    this.player.addEventListener?.(PlayerEvents.STREAM_CONFIG_LOAD_ERROR, this.onStreamConfigLoadError.bind(this));
    this.player.addEventListener?.(PlayerEvents.TRACK_CUE_POINT, this.onTrackCuePoint.bind(this));
  }

  private removeEventListeners(): void {
    if (!this.player) {
      return;
    }
    this.player.removeEventListener?.(PlayerEvents.STREAM_START, this.onStreamStart.bind(this));
    this.player.removeEventListener?.(PlayerEvents.STREAM_STOP, this.onStreamStop.bind(this));
    this.player.removeEventListener?.(PlayerEvents.STREAM_STATUS, this.onStreamStatusChange.bind(this));
    this.player.removeEventListener?.(PlayerEvents.STREAM_CONFIG_ERROR, this.onStreamConfigError.bind(this));
    this.player.removeEventListener?.(PlayerEvents.STREAM_FAIL, this.onStreamFail.bind(this));
    this.player.removeEventListener?.(PlayerEvents.STREAM_ERROR, this.onStreamError.bind(this));
    this.player.removeEventListener?.(PlayerEvents.STREAM_CONFIG_LOAD_ERROR, this.onStreamConfigLoadError.bind(this));
    this.player.removeEventListener?.(PlayerEvents.TRACK_CUE_POINT, this.onTrackCuePoint.bind(this));
  }

  /* Event listeners */
  private onStreamStart(): void {
    if (!this.isReady()) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
      name: HeartbeatEvents.START_HEARTBEAT,
      media: this.media,
    });
    EventDispatcher.sendEvent(PlayerEvents.STREAM_START, {
      name: PlayerEvents.STREAM_START,
      player: TRITON_PLAYER,
      isLiveAudio: true,
      uri: this.uri,
      media: this.media,
    });
  }

  private onStreamStop(): void {
    const media = this.previousMedia || this.media;

    EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
      name: HeartbeatEvents.STOP_HEARTBEAT,
      media,
    });
    EventDispatcher.sendEvent(PlayerEvents.STREAM_STOP, {
      name: PlayerEvents.STREAM_STOP,
      player: TRITON_PLAYER,
      media,
    });
    this.previousMedia = this.media;
  }

  private onStreamStatusChange(event: { data: any }): void {
    logger.log('STATUS', event.data);

    const statusCode = event?.data?.code;
    EventDispatcher.sendEvent(PlayerEvents.STREAM_STATUS, {
      name: statusCode,
      media: this.media,
    });

    switch (statusCode) {
      case 'LIVE_PLAYING':
        if (!TritonPlayer.active) {
          void this.stop();
        }
        break;

      case 'LIVE_STOP':
        break;

      case 'LIVE_BUFFERING':
        if (!TritonPlayer.active) {
          void this.stop();
        }

        break;

      case 'LIVE_CONNECTING':
        if (!TritonPlayer.active) {
          void this.stop();
        }
        break;

      case 'LIVE_FAILED':
      case 'PLAY_NOT_ALLOWED':
      case 'STREAM_GEO_BLOCKED':
      case 'STATION_NOT_FOUND':
        if (isOfflineOnFirefox() || this.isTritonUnmounted()) {
          return;
        }
        EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
          name: HeartbeatEvents.RESET_HEARTBEAT,
          media: this.media,
        });
        EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
          name: statusCode,
          ...this.config,
          streamUrl: this.station,
        });
        break;

      case 'LIVE_PAUSE':
        EventDispatcher.sendEvent(PlayerEvents.PLAYER_DISCONNECTED);
        EventDispatcher.sendEvent(PlayerEvents.STREAM_STOP, {
          name: PlayerEvents.STREAM_STOP,
          player: TRITON_PLAYER,
          media: this.media,
        });
        EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
          name: HeartbeatEvents.STOP_HEARTBEAT,
          media: this.media,
        });
        break;

      default:
        break;
    }
  }

  private onStreamConfigError(): void {
    if (this.isTritonUnmounted()) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
      name: PlayerEvents.STREAM_CONFIG_ERROR,
      ...this.config,
      streamUrl: this.station,
    });
  }

  private onStreamFail(): void {
    if (isOfflineOnFirefox() || this.isTritonUnmounted()) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
      name: PlayerEvents.STREAM_FAIL,
      ...this.config,
      streamUrl: this.station,
    });
  }

  private onStreamError(): void {
    if (this.isTritonUnmounted()) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
      name: PlayerEvents.STREAM_ERROR,
      ...this.config,
      streamUrl: this.station,
    });
  }

  private onStreamConfigLoadError(): void {
    if (this.isTritonUnmounted()) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
      name: PlayerEvents.STREAM_CONFIG_LOAD_ERROR,
      ...this.config,
      streamUrl: this.station,
    });
  }

  private onTrackCuePoint(event: { data: any }): void {
    if (!this.isReady()) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.TRACK_CUE_POINT, {
      name: PlayerEvents.TRACK_CUE_POINT,
      player: TRITON_PLAYER,
      cuePoint: event?.data,
    });
  }

  get duration(): number | undefined {
    const playerNode = this.getPlayerNode();
    if (!this.readyInternal || !playerNode) {
      return;
    }
    return playerNode.duration;
  }

  get volumeLevel(): number | undefined {
    if (!this.isReady()) {
      return 1;
    }

    const node = this.getPlayerNode();
    return node?.volume ?? undefined;
  }

  private getPlayerNode(): undefined | HTMLAudioElement {
    if (!this.isReady()) {
      return;
    }

    return this.player?.MediaElement?.audioNode;
  }

  get isPlaying(): boolean {
    if (!this.isReady()) {
      return false;
    }

    const node = this.getPlayerNode();
    return node ? !node.paused : false;
  }

  get currentTime(): number {
    if (!this.isReady()) {
      return 0;
    }

    const node = this.getPlayerNode();
    return node?.currentTime ?? 0;
  }

  get isMuted(): boolean {
    if (!this.isReady()) {
      return false;
    }

    const node = this.getPlayerNode();
    return node?.muted ?? false;
  }

  get isHls(): boolean {
    if (!this.isReady()) {
      return false;
    }
    return Boolean(this.media.audioHls);
  }

  readonly isLiveAudio = true;

  readonly playbackMode = PlaybackMode.LIVE_AUDIO;
}

export default TritonPlayer;
